import { useState, useMemo } from "react";
import axios, { API } from "../../api";
import { THEMES, THEME_GROUPS } from "../../constants";

/**
 * ThemePickerModal (Phase 30.G overhaul)
 * - 3 master groups (Light / Dark / Neutral) as tabs
 * - Multiple sub-themes per group (scrollable cards)
 * - Extensible: add a new group object to THEME_GROUPS and tagged themes to THEMES — no code edit here required
 * - When `inOnboarding` is true, picks immediately and skips the Apply button (used before the first wallpaper prompt)
 */
export function ThemePickerModal({
  onClose, isPremium, currentTheme, onThemeSelect, onUpgrade,
  dynamicMood, onDynamicMoodChange,
  inOnboarding = false,
}) {
  const initialGroup = useMemo(() => {
    const found = THEMES.find(t => t.id === currentTheme);
    return found?.group || 'dark';
  }, [currentTheme]);

  const [activeGroup, setActiveGroup] = useState(initialGroup);
  const [selectedTheme, setSelectedTheme] = useState(currentTheme);
  const [saving, setSaving] = useState(false);
  const [dynamicEnabled, setDynamicEnabled] = useState(Boolean(dynamicMood));
  const [savingDynamic, setSavingDynamic] = useState(false);

  const groupedThemes = useMemo(() => THEMES.filter(t => t.group === activeGroup), [activeGroup]);

  const handleApply = async () => {
    // Phase 30.Q — Themes are free for everyone (free trial + base + premium).
    // Removed the previous `if (!isPremium && !inOnboarding) onUpgrade()` short-
    // circuit so Apply always commits the theme.
    setSaving(true);
    try {
      // Onboarding path: also sync the beta-theme to match the selected group
      // so Aura's wallpapers + UI feel coherent from the first frame.
      try {
        document.documentElement.setAttribute("data-beta-theme", activeGroup);
        localStorage.setItem("aura_beta_theme", activeGroup);
      } catch {}
      // Phase 30.Q — Themes are free for everyone now. Always persist server-side.
      await axios.post(`${API}/user/theme`, { theme_id: selectedTheme });
      onThemeSelect(selectedTheme, activeGroup);
      document.documentElement.setAttribute('data-theme', selectedTheme);
      onClose();
    } catch (error) {
      console.error("Theme error:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDynamicToggle = async (next) => {
    setDynamicEnabled(next);
    setSavingDynamic(true);
    try {
      await axios.post(`${API}/user/preferences`, { dynamic_mood_theme: next });
      if (next) document.documentElement.setAttribute('data-dynamic-mood', 'true');
      else document.documentElement.removeAttribute('data-dynamic-mood');
      if (onDynamicMoodChange) onDynamicMoodChange(next);
    } catch (e) {
      console.error("dynamic mood pref", e);
      setDynamicEnabled(!next);
    } finally {
      setSavingDynamic(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="theme-picker-modal beta-theme-picker" onClick={(e) => e.stopPropagation()} data-testid="theme-picker-modal">
        <div className="journal-header">
          <h2>
            {inOnboarding ? '✨ Pick Aura\'s vibe' : '🎨 Themes'}
          </h2>
          {!inOnboarding && <button className="journal-close" onClick={onClose} data-testid="theme-close-btn">×</button>}
        </div>
        <p className="journal-subtitle">
          {inOnboarding
            ? 'Aura matches her wallpapers to the vibe you pick. You can always change this later.'
            : 'Choose your vibe'}
        </p>

        {!inOnboarding && (
          <label className="dynamic-mood-toggle" data-testid="dynamic-mood-toggle-row">
            <input
              type="checkbox"
              checked={dynamicEnabled}
              onChange={(e) => handleDynamicToggle(e.target.checked)}
              disabled={savingDynamic}
              data-testid="dynamic-mood-toggle"
            />
            <div className="dynamic-mood-toggle-text">
              <strong>✨ Match app theme to Aura's mood</strong>
              <span>The whole app shifts color as Aura's mood changes. Free for everyone.</span>
            </div>
          </label>
        )}

        {/* Phase 30.G — Group tabs (Light / Dark / Neutral, extensible) */}
        <div className="theme-group-tabs" role="tablist" data-testid="theme-group-tabs">
          {THEME_GROUPS.map(g => (
            <button
              key={g.id}
              role="tab"
              aria-selected={activeGroup === g.id}
              className={`theme-group-tab ${activeGroup === g.id ? 'is-active' : ''}`}
              onClick={() => setActiveGroup(g.id)}
              data-testid={`theme-group-${g.id}`}
            >
              <span className="theme-group-tab-emoji" aria-hidden>{g.emoji}</span>
              <span className="theme-group-tab-label">{g.label}</span>
              <span className="theme-group-tab-tagline">{g.tagline}</span>
            </button>
          ))}
        </div>

        {/* Phase 30.Q — Removed "Premium feature" lock banner. Themes are free for everyone. */}

        {/* Horizontally-scrollable theme cards within the active group */}
        <div className="themes-scroll-rail" data-testid="themes-scroll-rail">
          <div className="themes-scroll-inner">
            {groupedThemes.map((theme) => (
              <button
                key={theme.id}
                type="button"
                className={`theme-card-v2 ${selectedTheme === theme.id ? 'selected' : ''}`}
                onClick={() => setSelectedTheme(theme.id)}
                data-testid={`theme-${theme.id}`}
              >
                <div
                  className="theme-preview-v2"
                  style={{ background: `linear-gradient(135deg, ${theme.colors[0]}, ${theme.colors[1]}, ${theme.colors[2]})` }}
                >
                  <span className="theme-emoji-v2">{theme.emoji}</span>
                </div>
                <div className="theme-info-v2">
                  <strong>{theme.name}</strong>
                  <span>{theme.description}</span>
                </div>
                {selectedTheme === theme.id && <div className="theme-check-v2">✓</div>}
              </button>
            ))}
          </div>
        </div>

        <button
          className="init-btn beta-theme-apply"
          onClick={handleApply}
          disabled={saving}
          data-testid="apply-theme-btn"
        >
          {saving ? 'Applying…' : (inOnboarding ? '✨ Use this vibe' : 'Apply Theme')}
        </button>
      </div>
    </div>
  );
}
